import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Trophy, Flame, Crown, Medal, Award } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

const rankIcons = [Crown, Medal, Award];
const rankColors = ['#D4A54A', '#C0C0C0', '#CD7F32'];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLeaderboard(); }, []);

  const fetchLeaderboard = async () => {
    try {
      const { data } = await axios.get(`${API}/leaderboard`, { withCredentials: true });
      setLeaders(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="leaderboard-page">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-10">
          <motion.div variants={fadeUp} className="w-16 h-16 rounded-2xl bg-[#D4A54A]/10 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-[#D4A54A]" strokeWidth={1.5} />
          </motion.div>
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-2">Karma Leaderboard</motion.p>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Top Karma Doers</motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm text-[#A89070] mt-2">The ones who don't outsource their karma</motion.p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#D4A54A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leaders.length === 0 ? (
          <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-12 text-center">
            <Flame className="w-10 h-10 text-[#D4A54A]/30 mx-auto mb-4" />
            <p className="font-sans text-[#A89070]">No karma leaders yet. Be the first to log your karma!</p>
          </div>
        ) : (
          <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-3">
            {/* Top 3 podium */}
            {leaders.length >= 3 && (
              <motion.div variants={fadeUp} className="grid grid-cols-3 gap-3 mb-6">
                {[1, 0, 2].map((idx) => {
                  const l = leaders[idx];
                  if (!l) return null;
                  const Icon = rankIcons[idx];
                  const isFirst = idx === 0;
                  return (
                    <div key={l.user_id} className={`bg-[#1A1008] rounded-2xl border ${isFirst ? 'border-[#D4A54A]/50 gold-glow' : 'border-[#3D2D1D]/50'} p-5 text-center ${isFirst ? 'scale-105 -mt-4' : ''} transition-all`}>
                      <div className={`w-10 h-10 rounded-full ${isFirst ? 'bg-[#D4A54A]/20' : 'bg-[#3D2D1D]/50'} flex items-center justify-center mx-auto mb-3`}>
                        <Icon className="w-5 h-5" style={{ color: rankColors[idx] }} />
                      </div>
                      <div className="w-12 h-12 rounded-full bg-[#D4A54A]/10 flex items-center justify-center mx-auto mb-2 ring-2" style={{ borderColor: rankColors[idx] + '40' }}>
                        {l.picture ? (
                          <img src={l.picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="font-serif text-lg font-semibold" style={{ color: rankColors[idx] }}>{l.name?.[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <p className="font-sans text-sm font-medium text-[#F5E6C8] truncate">{l.name}</p>
                      <p className="font-serif text-xl font-semibold text-[#D4A54A] mt-1">{l.karma_points}</p>
                      <p className="font-sans text-xs text-[#A89070]">karma pts</p>
                    </div>
                  );
                })}
              </motion.div>
            )}

            {/* Full list */}
            {leaders.map((l, i) => {
              const rank = l.rank || (i + 1);
              const isMe = user?.user_id === l.user_id;
              return (
                <motion.div
                  key={l.user_id} variants={fadeUp}
                  data-testid={`leaderboard-rank-${rank}`}
                  className={`bg-[#1A1008] rounded-2xl border ${isMe ? 'border-[#D4A54A]/40 bg-[#D4A54A]/5' : 'border-[#3D2D1D]/50'} p-4 flex items-center gap-4 hover:border-[#D4A54A]/30 transition-all duration-200`}
                >
                  <div data-testid={`rank-number-${rank}`} className={`w-10 h-10 rounded-xl flex items-center justify-center font-sans text-sm font-bold flex-shrink-0 ${
                    rank <= 3 ? 'text-[#D4A54A] bg-[#D4A54A]/10' : 'text-[#A89070] bg-[#23180E]'
                  }`}>
                    #{rank}
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#D4A54A]/10 flex items-center justify-center flex-shrink-0">
                    {l.picture ? (
                      <img src={l.picture} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="font-sans text-sm font-semibold text-[#D4A54A]">{l.name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm font-medium text-[#F5E6C8] truncate">
                      {l.name} {isMe && <span className="text-[#D4A54A] text-xs">(You)</span>}
                    </p>
                    {l.city && <p className="font-sans text-xs text-[#A89070]">{l.city}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                      <Flame className="w-4 h-4 text-[#D4A54A]" />
                      <span className="font-serif text-xl font-semibold text-[#D4A54A]">{l.karma_points}</span>
                    </div>
                    <p className="font-sans text-xs text-[#A89070]">karma pts</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}
